import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('admin_token', data.access_token);
        localStorage.setItem('admin_token_expiry', Date.now() + (data.expires_in * 1000));
        if (onLogin) onLogin();
        navigate('/admin');
      } else {
        setError(data.detail || 'Error de autenticación');
      }
    } catch (err) {
      setError('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a2b3c] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-[#0d1620] rounded-2xl border border-[#50c878]/30 p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#50c878]/20 flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#50c878]" />
            </div>
            <h1 className="text-2xl font-bold text-[#f5f5dc] mb-2">Admin Panel</h1>
            <p className="text-[#f5f5dc]/50 text-sm">Los Legendarios</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[#f5f5dc]/70 text-sm mb-2">Usuario</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#50c878]" />
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-[#1a2b3c] border-[#50c878]/30 text-[#f5f5dc] placeholder:text-[#f5f5dc]/40 focus:border-[#50c878]"
                  placeholder="Nombre de usuario"
                  required
                  data-testid="admin-username"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#f5f5dc]/70 text-sm mb-2">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#50c878]" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-11 py-3 bg-[#1a2b3c] border-[#50c878]/30 text-[#f5f5dc] placeholder:text-[#f5f5dc]/40 focus:border-[#50c878]"
                  placeholder="Contraseña"
                  required
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#f5f5dc]/40 hover:text-[#f5f5dc]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#50c878] text-white hover:bg-[#40a0a0] font-medium"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                'Iniciar Sesión'
              )}
            </Button>
          </form>

          {/* Footer */}
          <p className="mt-6 text-center text-[#f5f5dc]/30 text-xs">
            © 2026 Versión Digital S.L.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
